import { motion } from 'framer-motion'
import { ChevronLeft, MoreVertical, Phone, Plus, Smile, Video } from 'lucide-react'

import { azi, fmtOra } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * A believable WhatsApp phone mockup — used on the Mesaje screen (and reusable
 * by the cinematic). Deliberately renders authentic WhatsApp chrome (teal
 * header, beige doodle wallpaper, tick marks) so a distributor looks at it and
 * says "that's my WhatsApp". The surrounding app stays warm-editorial; the
 * contrast is the point.
 */

export interface PhoneBubble {
  id: string
  side: 'in' | 'out'
  text: string
  ora?: string
  /** show the double blue read-tick (outgoing only, WhatsApp-style) */
  ticks?: boolean
}

// WhatsApp wallpaper doodle — a faint, tiled SVG evoking the pattern without
// copying it. Kept low-contrast so bubbles always dominate.
const DOODLE = (() => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'>
    <g fill='none' stroke='#cbb99f' stroke-width='1.5' stroke-linecap='round' opacity='0.35'>
      <circle cx='28' cy='30' r='7'/>
      <path d='M104 20v14M97 27h14'/>
      <path d='M18 108q11 -13 24 0'/>
      <circle cx='120' cy='104' r='5.5'/>
      <path d='M64 74l7 7M71 74l-7 7'/>
      <path d='M112 132h20'/>
      <path d='M40 66h14M47 59v14'/>
      <circle cx='86' cy='118' r='3.5'/>
    </g>
  </svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
})()

function Ticks() {
  return (
    <svg viewBox="0 0 18 12" className="inline-block h-3 w-4 align-middle" aria-hidden>
      <title>Citit</title>
      <path
        d="M1 6.5 4 9.5 10 2.5"
        fill="none"
        stroke="#53bdeb"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 6.5 10 9.5 16 2.5"
        fill="none"
        stroke="#53bdeb"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TypingDots() {
  return (
    <div className="flex items-end gap-1.5 rounded-2xl rounded-tl-sm bg-white px-3.5 py-3 shadow-[0_1px_1px_rgba(0,0,0,0.12)]">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="size-2 rounded-full bg-[#9aa0a6]"
          animate={{ y: [0, -3.5, 0], opacity: [0.35, 1, 0.35] }}
          transition={{
            duration: 0.9,
            repeat: Number.POSITIVE_INFINITY,
            delay: i * 0.16,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  )
}

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-6 pt-2.5 pb-1 text-[11px] font-semibold text-white/90 nums">
      <span>{fmtOra(azi())}</span>
      <div className="flex items-center gap-1.5">
        {/* signal */}
        <svg viewBox="0 0 18 12" className="h-2.5 w-4" aria-hidden>
          <title>Semnal</title>
          <g fill="currentColor">
            <rect x="0" y="8" width="3" height="4" rx="0.5" />
            <rect x="5" y="5" width="3" height="7" rx="0.5" />
            <rect x="10" y="2.5" width="3" height="9.5" rx="0.5" />
            <rect x="15" y="0" width="3" height="12" rx="0.5" opacity="0.45" />
          </g>
        </svg>
        {/* wifi */}
        <svg viewBox="0 0 16 12" className="h-2.5 w-3.5" aria-hidden>
          <title>Wi-Fi</title>
          <path
            d="M8 10.5a1 1 0 100-2 1 1 0 000 2zM3 5.5a7 7 0 0110 0M5 7.5a4 4 0 016 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        {/* battery */}
        <svg viewBox="0 0 26 12" className="h-2.5 w-6" aria-hidden>
          <title>Baterie</title>
          <rect
            x="0.5"
            y="1"
            width="22"
            height="10"
            rx="2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <rect x="2" y="2.5" width="17" height="7" rx="1.2" fill="currentColor" />
          <rect x="24" y="4" width="2" height="4" rx="1" fill="currentColor" />
        </svg>
      </div>
    </div>
  )
}

export interface PhoneMockProps {
  nume: string
  /** header subtitle, defaults to "online" */
  subtitle?: string
  initiale?: string
  bubbles: PhoneBubble[]
  typing?: boolean
  /** changing this key replays the entrance animation (pass the conversation id) */
  convKey?: string
  className?: string
}

export function PhoneMock({
  nume,
  subtitle = 'online',
  initiale,
  bubbles,
  typing = false,
  convKey,
  className,
}: PhoneMockProps) {
  const ini = initiale ?? initials(nume)
  return (
    <div className={cn('w-[340px] max-w-full select-none', className)}>
      {/* device shell */}
      <div className="relative rounded-[2.75rem] bg-[#0c0d10] p-2.5 shadow-paper-lg ring-1 ring-black/40">
        <div className="pointer-events-none absolute inset-0 rounded-[2.75rem] ring-1 ring-inset ring-white/10" />
        {/* screen */}
        <div className="relative flex h-[588px] flex-col overflow-hidden rounded-[2.25rem] bg-[#efe7de]">
          {/* notch */}
          <div className="absolute top-2 left-1/2 z-20 h-5 w-28 -translate-x-1/2 rounded-full bg-[#0c0d10]" />

          {/* header (teal) */}
          <div className="relative z-10 bg-gradient-to-b from-[#12907e] to-[#075e54] text-white shadow-[0_2px_6px_rgba(0,0,0,0.18)]">
            <StatusBar />
            <div className="flex items-center gap-2 px-3 pt-1.5 pb-2.5">
              <ChevronLeft className="size-5 shrink-0 opacity-90" />
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-semibold">
                {ini}
              </div>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate text-[15px] font-semibold">{nume}</div>
                <div className="truncate text-[11px] text-white/75">{subtitle}</div>
              </div>
              <Video className="size-[18px] shrink-0 opacity-90" />
              <Phone className="size-[17px] shrink-0 opacity-90" />
              <MoreVertical className="size-[18px] shrink-0 opacity-90" />
            </div>
          </div>

          {/* chat area */}
          <div
            className="relative flex-1 overflow-y-auto px-3 py-3"
            style={{ backgroundColor: '#efe7de', backgroundImage: DOODLE }}
          >
            {/* encryption notice */}
            <div className="mx-auto mb-3 w-fit max-w-[85%] rounded-md bg-[#fdf4c9] px-2.5 py-1.5 text-center text-[10.5px] leading-snug text-[#5b5545] shadow-[0_1px_1px_rgba(0,0,0,0.08)]">
              Mesajele sunt criptate integral. Nimeni din afara acestei conversații nu le poate
              citi.
            </div>

            <div key={convKey} className="flex flex-col gap-1.5">
              {bubbles.map((b, i) => (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.32, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className={cn('flex', b.side === 'out' ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={cn(
                      'relative max-w-[82%] rounded-2xl px-3 py-2 text-[13.5px] leading-relaxed text-[#111b21] shadow-[0_1px_1px_rgba(0,0,0,0.13)]',
                      b.side === 'out' ? 'rounded-tr-sm bg-[#d9fdd3]' : 'rounded-tl-sm bg-white',
                    )}
                  >
                    <span className="whitespace-pre-wrap">{b.text}</span>
                    <span className="float-right mt-1 ml-2 flex translate-y-0.5 items-center gap-0.5 text-[10px] text-[#667781] nums">
                      {b.ora}
                      {b.side === 'out' && b.ticks && <Ticks />}
                    </span>
                  </div>
                </motion.div>
              ))}

              {typing && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <TypingDots />
                </motion.div>
              )}
            </div>
          </div>

          {/* input bar */}
          <div className="flex items-center gap-2 bg-[#efe7de] px-2.5 pt-1.5 pb-3">
            <div className="flex flex-1 items-center gap-2 rounded-full bg-white px-3 py-2 shadow-[0_1px_1px_rgba(0,0,0,0.1)]">
              <Smile className="size-[18px] shrink-0 text-[#8696a0]" />
              <span className="flex-1 truncate text-[13px] text-[#8696a0]">Scrie un mesaj</span>
              <Plus className="size-[18px] shrink-0 text-[#8696a0]" />
            </div>
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#12907e] text-white shadow-[0_1px_2px_rgba(0,0,0,0.2)]">
              {/* mic glyph */}
              <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
                <title>Microfon</title>
                <path
                  fill="currentColor"
                  d="M12 15a3 3 0 003-3V6a3 3 0 00-6 0v6a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.92V22h2v-3.08A7 7 0 0019 12h-2z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return (words[0]?.slice(0, 2) ?? '?').toUpperCase()
  return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase()
}
